<template>
  <button
    v-show="!takenPositionIds.includes(position.id)"
    class="net-placeholder"
    type="button"
    :style="{
      transform: `translate3d(${position.x}px, ${position.y}px, 0)`
    }"
    @click="onClick"
    @pointerdown="onMouseDown"
    @contextmenu.prevent="onContextMenu"
  >
    <div class="net-placeholder__plus"></div>
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useTemplateStore } from "@editor/items/template/templates.store";

import type { Handleable } from "@core/types/Events";
import { toUid, type Uid } from "@/helpers/utility/Id";
import { useSceneElementStore } from "../sceneElement/sceneElements.store";

type Position = {
  id: number;
  x: number;
  y: number;
};

const props = defineProps<{ position: Position }>();
const emit = defineEmits<{
  (
    e: "contextmenu",
    event: MouseEvent,
    data: { uid: Uid<"elementPlaceholder"> }
  ): void;
}>();

const takenPositionIds = computed(() => {
  return useTemplateStore().takenPositionIds;
});

async function createTarget() {
  const { x, y } = props.position;

  const elementNode = await useSceneElementStore().create({
    x, y, width: 200, height: 90
  }, {
    type: "target",
    title: ""
  });

  useSelectionStore().select(elementNode);
}

// ==================
// Event handlers

function onClick(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.hasBeenHandled = true;
  createTarget();
}

function onMouseDown(mouseEvent: MouseEvent & Handleable) {
  // don't handle middle mouse key.
  if (mouseEvent.which === 2) {
    return;
  }
  mouseEvent.hasBeenHandled = true;
}

function onContextMenu(event: MouseEvent) {
  emit("contextmenu", event, {
    uid: toUid("elementPlaceholder", props.position.id)
    // position: props.position
  });
}
</script>

<style lang="scss">
@use "sass:math";

.net-placeholder {
  $width: 200px;
  $height: 90px;

  appearance: none;
  pointer-events: auto;
  opacity: 0.2;
  width: $width;
  height: $height;
  color: var(--color-dark);
  border: 1px solid currentColor;
  background-color: var(--color-background-close);
  position: absolute;
  border-radius: 4px;

  &:hover {
    background-color: var(--color-background-close-hover);
    opacity: 0.3;
  }
  &:focus-visible {
    opacity: 0.4;
    color: var(--color-action);
    outline: 1px solid currentColor;
  }

  &__plus {
    opacity: 0;

    $linewidth: 4px;
    $linelength: 26px;

    &::before,
    &::after {
      content: "";
      position: absolute;
      display: block;
      height: $linelength;
      width: $linewidth;
      background-color: currentColor;
    }
    &::before {
      height: $linelength;
      width: $linewidth;
      top: math.div($height, 2);
      left: math.div($width, 2);
      margin-left: math.div(-$linewidth, 2);
      margin-top: math.div(-$linelength, 2);
    }
    &::after {
      width: $linelength;
      height: $linewidth;
      top: math.div($height, 2);
      left: math.div($width, 2);
      margin-top: math.div(-$linewidth, 2);
      margin-left: math.div(-$linelength, 2);
    }
  }

  &:hover &__plus,
  &:focus-visible &__plus {
    transition: opacity 80ms ease-out;
    opacity: 1;
  }
}
</style>
